import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, TableItem } from 'components/atoms';
import LabeledIncomeStatus from 'components/molecules/labeledStatus/LabaleIncomeStatus';
import { Table } from 'components/organisms';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarPage } from 'components/templates';
import { useParamId } from 'hooks/useParamId';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTranslation } from 'react-i18next';
import { PiCaretLeftBold } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { INCOME_TYPE } from 'services/cash-flow/income/types';
import { useGetLabelBalanceHistory } from 'services/settlement';
import { BALANCE_TYPE, IFinancesDetail } from 'services/settlement/types';
import { formatCurrency } from 'utils/global/currency.utils';
import { getMaxFormattedLength } from 'utils/global/getMaxFormattedLength.utils';
import { getNodes } from 'utils/table/nodes.utils';

const LabelBalanceHistory = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  usePermissionGuard('SettlementCostPermissionsLIST**');
  usePermissionGuard('SettlementIncomePermissionsLIST**');

  const labelId = useParamId('label');

  const labelBalanceHistory = useGetLabelBalanceHistory(labelId);

  const tableData = getNodes<IFinancesDetail>(
    labelBalanceHistory.data?.pages.flat() || []
  );

  const amountNetLength = getMaxFormattedLength(
    tableData?.nodes ?? [],
    e => e?.amount?.netAmount
  );
  const amountGrossLength = getMaxFormattedLength(
    tableData?.nodes ?? [],
    e => e?.amount?.grossAmount
  );

  const amountToRepaymentLength = getMaxFormattedLength(
    tableData?.nodes ?? [],
    e => e?.amountToRepayment
  );

  const taxLength = getMaxFormattedLength(tableData?.nodes ?? [], e => e?.tax);

  const onSelectChange = (action: Action, state: State) => {
    const rowData = tableData.nodes || [];

    const selectedRow = rowData.find(row => row.id === action.payload.id);

    if (selectedRow?.type === 1) {
      navigate(`/label/${labelId}/cost/${selectedRow?.id}/file`);
    } else {
      navigate(`/label/${labelId}/income/${selectedRow?.id}/file`);
    }
  };

  const select = useRowSelect(tableData, {
    onChange: onSelectChange,
  });

  return (
    <TopbarPage
      topbar={
        <TopBar
          size='compact'
          title={t('history.balance')}
          customBackItem={
            <Button
              isClear
              onlyIcon
              onClick={() => navigate(`/label/${labelId}/balance`)}>
              <Button.Icon icon={<PiCaretLeftBold />} />
            </Button>
          }
        />
      }>
      <Table
        id={'LabelBalanceHistory'}
        size='compact'
        data={tableData}
        select={select}
        onFullyScroll={labelBalanceHistory.fetchNextPage}
        loading={
          labelBalanceHistory.isFetchingNextPage ||
          labelBalanceHistory.isLoading
        }
        columns={[
          {
            label: t('input.label.name'),
            renderCell: ({ name }) => (
              <TableItem>
                <TableItem.Text text={name} />
              </TableItem>
            ),
            resize: true,
          },

          {
            label: t('input.label.amountToRepayment'),
            renderCell: ({ type, amountToRepayment }) => (
              <TableItem>
                <TableItem.Currency
                  text={
                    type === BALANCE_TYPE.cost
                      ? formatCurrency(amountToRepayment, {
                          length: amountToRepaymentLength,
                        })
                      : undefined
                  }
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.net'),
            renderCell: ({ amount }) => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(amount.netAmount, {
                    length: amountNetLength,
                  })}
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.gross'),
            renderCell: ({ amount }) => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(amount.grossAmount, {
                    length: amountGrossLength,
                  })}
                />
              </TableItem>
            ),
            resize: true,
          },
          {
            label: t('input.label.tax'),
            renderCell: ({ tax }) => (
              <TableItem>
                <TableItem.Currency
                  text={`${formatCurrency(tax, {
                    length: taxLength,
                    suffix: '%',
                  })} `}
                />
              </TableItem>
            ),
            resize: true,
          },

          {
            label: t('input.label.type'),
            renderCell: ({ incomeType, type }) => (
              <TableItem>
                <LabeledIncomeStatus
                  status={
                    type === BALANCE_TYPE.income
                      ? incomeType === INCOME_TYPE.cost
                        ? INCOME_TYPE.basic
                        : incomeType
                      : INCOME_TYPE.cost
                  }
                />
              </TableItem>
            ),
            resize: true,
          },
        ]}
      />
    </TopbarPage>
  );
};

export default LabelBalanceHistory;
